import React, { FC, useEffect, useState } from 'react'
import {
    Drawer,
    Box,
    Typography,
    Stepper,
    Step,
    StepLabel,
    IconButton,
    Paper,
    Tooltip,
    Skeleton,
    Chip,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import DoneAllOutlinedIcon from '@mui/icons-material/DoneAllOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import { Ticket } from '../../../../types/slices/ticketType'
import { useTicketStore } from '../../../../hooks/useTicketStore'

const icons = [
    { id: 1, icon: <AddCircleOutlineIcon fontSize="small" /> },
    { id: 2, icon: <CheckCircleOutlineIcon fontSize="small" /> },
    { id: 3, icon: <DescriptionOutlinedIcon fontSize="small" /> },
    { id: 4, icon: <LocalShippingOutlinedIcon fontSize="small" /> },
    { id: 5, icon: <DoneAllOutlinedIcon fontSize="small" /> },
]

interface TrackerTicketDrawerProps {
    open: boolean
    onClose: () => void
    pedido: Ticket
}

export const TrackerTicketDrawer: FC<TrackerTicketDrawerProps> = ({ open, onClose, pedido }) => {
    const { getTrackerTicket } = useTicketStore()
    const [steps, setSteps] = useState<any[]>([])
    const [loading, setLoading] = useState(true)
    const [openNC, setOpenNC] = useState(false)
    const [selectedNC, setSelectedNC] = useState<any[]>([])

    useEffect(() => {
        getDataAPI()
    }, [])

    const getDataAPI = async () => {
        setLoading(true)
        const resp = await getTrackerTicket(pedido?.id)
        if (resp) {
            setSteps(resp)
        }
        setLoading(false)
    }

    const getIcon = (id: number) => {
        const icon = icons.find((icon) => icon.id === id)
        return icon ? icon.icon : null
    }

    const activeStep = (() => {
        const index = steps?.findIndex(step => step.complete === 0)
        return index === -1 ? steps?.length : index
    })()

    const prepareNCWithAcumulados = () => {
        const acumuladoPorProducto: Record<number, number> = {}
        const sortedNC = [...selectedNC]
        return sortedNC.map(nc => {
            const detallesConCantidades = nc.details.map((d: any) => {
                const idProd = d.idproduct
                if (!(idProd in acumuladoPorProducto)) {
                    acumuladoPorProducto[idProd] = pedido?.details?.find(row => row.idproduct === idProd)?.amount ?? 0
                }
                const cantidadFinal = acumuladoPorProducto[idProd]
                const cantidadDevuelta = d.amount
                const cantidadInicial = cantidadFinal + cantidadDevuelta
                acumuladoPorProducto[idProd] = cantidadInicial
                return { ...d, cantidadInicial, cantidadFinal }
            })
            return { ...nc, details: detallesConCantidades }
        })
    }

    const selectedNCConCantidades = prepareNCWithAcumulados()

    return (
        <>
            {/* Drawer principal */}
            <Drawer
                anchor="right"
                open={open}
                onClose={onClose}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', sm: 500 },
                        borderRadius: '12px 0 0 12px',
                        backgroundColor: '#fafafa',
                        boxShadow: '-4px 0 15px rgba(0,0,0,0.08)'
                    },
                }}
            >
                <Box sx={{ p: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                        <Typography variant="h6" fontWeight={700} color="primary">
                            Seguimiento del Pedido #{pedido?.nro_ticket}
                        </Typography>
                        <IconButton onClick={onClose} size="small" sx={{ bgcolor: 'grey.100', '&:hover': { bgcolor: 'grey.200' } }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {loading ? (
                        <Box>
                            {[...Array(5)].map((_, i) => (
                                <Skeleton
                                    key={i}
                                    variant="rectangular"
                                    height={64}
                                    sx={{ borderRadius: 2, mb: 2 }}
                                />
                            ))}
                        </Box>
                    ) : (
                        <Stepper activeStep={activeStep} orientation="vertical" sx={{ '& .MuiStepConnector-line': { borderLeft: '2px dashed #ddd' } }}>
                            {steps?.map((step, index) => {
                                const isActive = index === activeStep
                                const isCompleted = step.complete === 1
                                const hasNC = step?.ticketsNC?.length > 0

                                return (
                                    <Step key={step.label}>
                                        <StepLabel
                                            StepIconProps={{
                                                sx: { color: isCompleted || isActive ? '#516E80' : '#ccc' },
                                            }}
                                        >
                                            <Paper
                                                elevation={isActive ? 3 : 0}
                                                sx={{
                                                    px: 2,
                                                    py: 1.5,
                                                    borderRadius: 2,
                                                    backgroundColor: isActive ? '#f1f6f8' : '#fff',
                                                    borderLeft: isActive ? '4px solid #516E80' : '4px solid transparent',
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': { backgroundColor: '#f9fbfc' }
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Box
                                                            sx={{
                                                                bgcolor: isCompleted || isActive ? '#516E80' : '#ccc',
                                                                color: '#fff',
                                                                p: 0.7,
                                                                borderRadius: '50%',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                            }}
                                                        >
                                                            {getIcon(step.id)}
                                                        </Box>
                                                        <Typography
                                                            variant="body1"
                                                            sx={{
                                                                fontWeight: isActive ? 600 : 500,
                                                                color: isCompleted || isActive ? '#333' : '#999',
                                                            }}
                                                        >
                                                            {step.label}
                                                        </Typography>
                                                        {hasNC && (
                                                            <Chip
                                                                label={`${step.ticketsNC.length} NC`}
                                                                size="small"
                                                                color="warning"
                                                                sx={{ fontWeight: 600 }}
                                                            />
                                                        )}
                                                    </Box>

                                                    {hasNC && (
                                                        <Tooltip title="Ver notas de crédito">
                                                            <IconButton
                                                                size="small"
                                                                sx={{ color: '#516E80' }}
                                                                onClick={() => {
                                                                    setSelectedNC(step.ticketsNC)
                                                                    setOpenNC(true)
                                                                }}
                                                            >
                                                                <ReceiptLongOutlinedIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    {step.label === 'Factura emitida' && isCompleted && pedido?.url_invoice && (
                                                        <Tooltip title="Ver factura">
                                                            <IconButton
                                                                size="small"
                                                                href={pedido.url_invoice}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                sx={{ color: '#516E80' }}
                                                            >
                                                                <VisibilityOutlinedIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>

                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: '#666',
                                                        ml: '36px',
                                                        mt: 0.5,
                                                        display: 'block',
                                                    }}
                                                >
                                                    {step.complete === 1
                                                        ? new Date(step?.date).toLocaleString('es-CL', {
                                                            day: '2-digit',
                                                            month: 'short',
                                                            year: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                        })
                                                        : (isActive ? 'En proceso...' : '')
                                                    }
                                                </Typography>
                                            </Paper>
                                        </StepLabel>
                                    </Step>
                                )
                            })}
                        </Stepper>
                    )}
                </Box>
            </Drawer>

            {/* Drawer de Notas de Crédito */}
            <Drawer
                anchor="right"
                open={openNC}
                onClose={() => setOpenNC(false)}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', sm: 600 },
                        borderRadius: '12px 0 0 12px',
                        backgroundColor: '#fff',
                        p: 3
                    }
                }}
            >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" fontWeight={700} color="primary">
                        Notas de Crédito ({selectedNCConCantidades.length})
                    </Typography>
                    <IconButton onClick={() => setOpenNC(false)} size="small" sx={{ bgcolor: 'grey.100', '&:hover': { bgcolor: 'grey.200' } }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {selectedNCConCantidades.map((nc) => (
                    <Paper
                        key={nc.id}
                        sx={{
                            p: 2,
                            mb: 2,
                            border: '1px solid #eee',
                            borderRadius: 2,
                            boxShadow: '0 1px 4px rgba(0,0,0,0.04)'
                        }}
                    >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle1" fontWeight={600}>
                                Folio {nc.folio}
                            </Typography>
                            <IconButton
                                size="small"
                                href={nc.url_invoice}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ color: '#516E80' }}
                            >
                                <VisibilityOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            {nc.date_format} — {nc.payment_status_name}
                        </Typography>
                        <Typography variant="body2" fontWeight={600} mt={1}>
                            Total: ${nc.total}
                        </Typography>

                        <Table size="small" sx={{ mt: 1 }}>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: 'grey.50' }}>
                                    <TableCell>Producto</TableCell>
                                    <TableCell>Código</TableCell>
                                    <TableCell align="right">Cant.</TableCell>
                                    <TableCell align="right">Precio</TableCell>
                                    <TableCell align="right">Subtotal</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {nc.details.map((d: any) => (
                                    <TableRow key={d.id}>
                                        <TableCell>{d.product}</TableCell>
                                        <TableCell>{d.code_system}</TableCell>
                                        <TableCell align="right">{d.amount}</TableCell>
                                        <TableCell align="right">${d.price}</TableCell>
                                        <TableCell align="right">${d.gross_subtotal}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>

                        <Typography variant="subtitle1" fontWeight={600} mt={2}>
                            Movimientos
                        </Typography>
                        <Table
                            size="small"
                            sx={{
                                mt: 1,
                                borderRadius: 1,
                                overflow: 'hidden',
                                '& th': { backgroundColor: 'grey.100', fontWeight: 600 },
                                '& td, & th': { borderBottom: '1px solid #eee' },
                                '& tr:nth-of-type(even)': { backgroundColor: 'grey.50' },
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell>Producto</TableCell>
                                    <TableCell>Código</TableCell>
                                    <TableCell align="right">Inicial</TableCell>
                                    <TableCell align="right" sx={{ color: 'error.main' }}>Devuelto</TableCell>
                                    <TableCell align="right" sx={{ color: 'success.main' }}>Final</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {nc.details.map(d => (
                                    <TableRow key={d.id}>
                                        <TableCell>{d.product}</TableCell>
                                        <TableCell>{d.code_system}</TableCell>
                                        <TableCell align="right">{d.cantidadInicial}</TableCell>
                                        <TableCell align="right" sx={{ color: 'error.main', fontWeight: 600 }}>
                                            -{d.amount}
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: 'success.main', fontWeight: 600 }}>
                                            {d.cantidadFinal}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                ))}
            </Drawer>
        </>
    )
}
